import React, { useState } from 'react'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableBody from '@mui/material/TableBody'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import Checkbox from '@mui/material/Checkbox'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import Chip from '@mui/material/Chip'
import Tooltip from '@mui/material/Tooltip'
import IconButton from '@mui/material/IconButton'
import Popover from '@mui/material/Popover'
import Menu from '@mui/material/Menu'
import Avatar from '@mui/material/Avatar'
import Button from '@mui/material/Button'
import { NotesIcon, BlockIcon, InfoIcon } from '../../../components/Icons/Icons'
import TableSkeletonRows from '../../../components/Skeleton/TableSkeletonRows'
import useURLState from '../../../hooks/useURLState'
import { formatMoney } from '../../../utils/amount'
import Divider from '@mui/material/Divider'
import ListSubheader from '@mui/material/ListSubheader'
import {
  QUICK_FILTERS,
  SKIPPED_FILTER_KEY,
  SIDEBAR_FILTER_LABELS,
  getInitials,
  getAttempts,
  getEffectiveSelectValue,
  getRowGatewayOptions,
  getStatusColorKey,
  getBandColorKey,
  getLastPaymentColorKey,
  getPlanStatusColorKey,
  formatPercent,
  formatDate,
  formatPlanStatus,
  routeSourceLabel,
  gatewayLabel,
  paymentLabel,
  EM_DASH,
} from './helpers'

// The visible page AND sort live in the URL (?page=, ?sort=), like every other list screen in the
// app (Leads, Affiliates, Contacts via useURLState). That puts them in the browser history entry,
// so opening a client from the kebab and pressing Back restores the reviewer's place instead of
// snapping to the top. Module-level so its identity is stable across renders.
const VIEW_URL_STATE = {
  page: { type: 'number', default: 1 },
  sort: { type: 'string', default: 'default' },
}

const CHIP_COLOR = { success: 'success', warning: 'warning', error: 'error', default: 'default' }
const TEXT_COLOR = {
  success: 'text-success-main',
  warning: 'text-warning-main',
  error: 'text-error-main',
  default: 'text-text-secondary',
}

const RowNotes = ({ row, onOpenNotes }) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const hasNotes = !!row.latestNote
  const unread = (row.notesCount || 0) > 0

  return (
    <>
      <Tooltip title="Notes">
        <span className="relative inline-flex">
          <IconButton size="small" aria-label={`notes for ${row.clientName}`} onClick={(e) => setAnchorEl(e.currentTarget)}>
            <NotesIcon className="w-4 h-4 text-gray-500" width={18} height={18} />
          </IconButton>
          {unread && (
            <span
              className="absolute top-1 right-1 w-2 h-2 rounded-full bg-error-main"
              aria-label="unread note"
              data-testid={`unread-dot-${row.paymentId}`}
            />
          )}
        </span>
      </Tooltip>
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <div className="p-3 max-w-[280px]">
          {hasNotes ? (
            <p className="text-sm text-text-primary">{row.latestNote}</p>
          ) : (
            <p className="text-sm text-text-secondary">No notes yet.</p>
          )}
          <button
            type="button"
            className="mt-2 text-xs font-medium text-primary-main hover:underline"
            data-testid={`open-notes-${row.paymentId}`}
            onClick={() => {
              setAnchorEl(null)
              onOpenNotes?.(row)
            }}
          >
            {hasNotes ? 'View all / add note →' : 'Add a note →'}
          </button>
        </div>
      </Popover>
    </>
  )
}

const RowActionMenu = ({ row, onViewClient }) => {
  const [anchorEl, setAnchorEl] = useState(null)
  return (
    <>
      <Tooltip title="More actions">
        <IconButton size="small" aria-label={`actions for ${row.clientName}`} onClick={(e) => setAnchorEl(e.currentTarget)}>
          <span className="text-lg leading-none text-text-secondary">&#8230;</span>
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)}>
        <MenuItem
          onClick={() => {
            setAnchorEl(null)
            onViewClient(row)
          }}
        >
          View client
        </MenuItem>
      </Menu>
    </>
  )
}

// "Select by processor" bulk control: lists the processors present among the visible,
// selectable (Ready) rows with per-processor Ready counts, and selects that processor's
// Ready rows only (deselecting the rest). Disabled when no Ready rows are present.
const SelectByProcessorMenu = ({ groups = [], onSelectProcessor }) => {
  const [anchorEl, setAnchorEl] = useState(null)
  return (
    <>
      <Button
        size="small"
        variant="outlined"
        data-testid="select-by-processor"
        disabled={groups.length === 0}
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        Select by processor
      </Button>
      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)}>
        {groups.map((group) => (
          <MenuItem
            key={group.key}
            data-testid={`select-processor-${group.key}`}
            onClick={() => {
              setAnchorEl(null)
              onSelectProcessor?.(group.key)
            }}
          >
            {group.label} ({group.count})
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}

// Backup-gateway transparency (LawPay Part B, Task 6): shown ONLY when the backend
// has set row.backupGateway — i.e. this card payment's company has a backup processor
// configured, and the charge will auto-fall-back to it if the primary (Mint)
// soft-declines. Purely informational (not a control), so it's a plain Tooltip+span
// like KpiCards' info icon, not an IconButton.
const RowBackupGatewayNote = ({ row }) => (
  <Tooltip
    title={`Backup: ${gatewayLabel(row.backupGateway)} — auto-charges if the primary soft-declines`}
    arrow
    placement="top"
  >
    <span
      className="inline-flex cursor-help"
      aria-label={`backup gateway for ${row.clientName}`}
      data-testid={`backup-gateway-icon-${row.paymentId}`}
    >
      <InfoIcon fill="var(--color-info-main)" />
    </span>
  </Tooltip>
)

// Dedicated per-row Skip action, dispatching the SAME override the processor
// dropdown's "Skip (do not process)" option sets — this is just a faster,
// more discoverable path to that existing mechanism, not a new one.
const RowSkipButton = ({ row, onOverrideChange }) => (
  <Tooltip title="Skip (do not process)">
    <IconButton
      size="small"
      aria-label={`skip payment for ${row.clientName}`}
      data-testid={`skip-btn-${row.paymentId}`}
      onClick={() => onOverrideChange(row, 'skip')}
    >
      <BlockIcon />
    </IconButton>
  </Tooltip>
)

// Memoized so a selection change re-renders ONE row instead of the whole table. Every prop
// is a primitive or a stable reference: `checked` is a boolean rather than the shared
// `selection` object, and `selectValue` / `result` are resolved per row by the parent — passing
// `overrides` or `resultsById` wholesale would change identity on every keystroke and defeat
// the memo entirely. The handlers are useCallback'd in DailyProcessing for the same reason.
const PaymentRow = React.memo(function PaymentRow({
  row,
  checked,
  selectValue,
  result,
  onToggleRow,
  onOverrideChange,
  onViewClient,
  onOpenNotes,
}) {
  const attempts = getAttempts(row)
  const statusColorKey = getStatusColorKey(row.status)
  const planStatusColorKey = getPlanStatusColorKey(row.planStatus)
  const bandColorKey = getBandColorKey(row.forecast?.band)
  const lastPaymentColorKey = getLastPaymentColorKey(row.lastPayment?.status)
  const gatewayOptions = getRowGatewayOptions(row)
  const methodLabel = row.billingMethod?.type || row.recommendedMethod || ''

  return (
    <TableRow key={row.paymentId} hover data-testid={`row-${row.paymentId}`}>
      <TableCell padding="checkbox">
        <Checkbox
          checked={checked}
          onChange={() => onToggleRow(row)}
          inputProps={{ 'aria-label': `select ${row.clientName}` }}
        />
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2.5">
          <Avatar sx={{ width: 32, height: 32, fontSize: 13 }}>{getInitials(row.clientName)}</Avatar>
          <div>
            <p className="text-sm font-medium text-text-primary">{row.clientName}</p>
            <p className="text-xs text-text-secondary">{row.clientCode}</p>
            {typeof row.chargeAs === 'string' && row.chargeAs.length > 0 && (
              <p className="text-xs text-text-secondary">{`Charge as: ${row.chargeAs}`}</p>
            )}
          </div>
        </div>
      </TableCell>
      <TableCell>
        <span className="text-sm font-medium text-text-primary">{formatMoney(row.amount)}</span>
      </TableCell>
      <TableCell>
        <p className="text-sm text-text-primary">{paymentLabel(row)}</p>
        {row.planStartDate && (
          <p className="text-xs text-text-secondary">Started {formatDate(row.planStartDate)}</p>
        )}
      </TableCell>
      <TableCell data-testid={`due-date-${row.paymentId}`}>
        {/* This payment's own due date — with overdue + upcoming spanning many days, the operator
            must be able to read each row's due day. Business time (see formatDate/formatBusinessDate),
            never the viewer's browser timezone. */}
        <span className="text-sm text-text-primary">{formatDate(row.dueDate)}</span>
      </TableCell>
      <TableCell>
        {row.lastPayment?.date ? (
          <>
            <p className="text-sm text-text-primary">{formatDate(row.lastPayment.date)}</p>
            {row.lastPayment.status && (
              <p className={`text-xs font-medium ${TEXT_COLOR[lastPaymentColorKey]}`}>
                {row.lastPayment.status}
              </p>
            )}
          </>
        ) : (
          <span className="text-sm text-text-secondary">{EM_DASH}</span>
        )}
      </TableCell>
      <TableCell>
        <Chip
          size="small"
          label={row.status}
          color={CHIP_COLOR[statusColorKey]}
          variant={statusColorKey === 'default' ? 'outlined' : 'filled'}
        />
        {row.routeFallbackFrom && (
          <p className="text-[11px] mt-1 text-warning-main" data-testid={`route-guessed-${row.paymentId}`}>
            Gateway guessed: this account stores "{row.routeFallbackFrom}", which nobody configured.
          </p>
        )}
        {/* WS 2146091: the server holds this row because its last charge never confirmed. */}
        {row.unconfirmedCharge && (
          <p className="text-[11px] mt-1 text-warning-main" data-testid={`unconfirmed-charge-${row.paymentId}`}>
            Last charge never confirmed: check the gateway before charging again.
          </p>
        )}
        {result && (
          <p className={`text-[11px] mt-1 ${result.ok ? 'text-success-main' : 'text-error-main'}`}>
            {result.ok ? `Result: ${result.status}` : `Failed: ${result.error || result.status}`}
          </p>
        )}
      </TableCell>
      <TableCell data-testid={`plan-status-${row.paymentId}`}>
        <Chip
          size="small"
          label={formatPlanStatus(row.planStatus)}
          color={CHIP_COLOR[planStatusColorKey]}
          variant={planStatusColorKey === 'default' ? 'outlined' : 'filled'}
        />
      </TableCell>
      <TableCell>
        <Tooltip title={row.forecast?.basis || 'No basis provided'}>
          <span className="inline-flex flex-col cursor-help">
            <span className="text-sm font-medium text-text-primary">
              {formatPercent(row.forecast?.successRate)}
            </span>
            <span className={`text-xs ${TEXT_COLOR[bandColorKey]}`}>{row.forecast?.band || 'Unknown'}</span>
          </span>
        </Tooltip>
      </TableCell>
      <TableCell>
        <div className="flex flex-col gap-1 items-start">
          <div className="flex items-center gap-1">
            {methodLabel && (
              <span className="text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                {methodLabel}
              </span>
            )}
            {row.backupGateway && <RowBackupGatewayNote row={row} />}
          </div>
          <Select
            size="small"
            value={selectValue}
            onChange={(e) => onOverrideChange(row, e.target.value)}
            inputProps={{ 'aria-label': `processor for ${row.clientName}` }}
            data-testid={`processor-select-${row.paymentId}`}
            sx={{ minWidth: 180 }}
            renderValue={(value) => {
              if (value === 'skip') return 'Skip'
              const opt = gatewayOptions.find((o) => o.value === value)
              return opt ? opt.label : value || '—'
            }}
          >
            <ListSubheader className="!text-[11px] !leading-6">{methodLabel || 'Gateway'}</ListSubheader>
            {gatewayOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
                {opt.recommended && (
                  <span className="ml-1.5 text-[11px] text-text-secondary">(recommended)</span>
                )}
              </MenuItem>
            ))}
            {gatewayOptions.length === 0 && (
              <MenuItem value="" disabled>
                No gateway configured
              </MenuItem>
            )}
            <Divider />
            <MenuItem value="skip">Skip (do not process)</MenuItem>
          </Select>
          {/* WHERE the preselected gateway came from (company default / account / first-N)
              — routeSource from the shared routing rule; absent on older rows → no chip. */}
          {routeSourceLabel(row.routeSource) && (
            <span
              className="text-[10px] text-text-secondary"
              data-testid={`route-source-${row.paymentId}`}
            >
              via {routeSourceLabel(row.routeSource)}
            </span>
          )}
        </div>
      </TableCell>
      <TableCell align="center" data-testid={`attempts-${row.paymentId}`}>{attempts}</TableCell>
      <TableCell align="center">
        <RowNotes row={row} onOpenNotes={onOpenNotes} />
      </TableCell>
      <TableCell align="center">
        <div className="flex items-center justify-center gap-1">
          <RowSkipButton row={row} onOverrideChange={onOverrideChange} />
          <RowActionMenu row={row} onViewClient={onViewClient} />
        </div>
      </TableCell>
    </TableRow>
  )
})

const PaymentsTable = ({
  rows,
  selection,
  overrides,
  resultsById = {},
  counts = {},
  quickFilter = 'all',
  allVisibleSelected,
  someVisibleSelected,
  cleanCount,
  processorGroups = [],
  loading = false,
  totalRows,
  onChangeFilter,
  onToggleRow,
  onToggleAllVisible,
  onSelectAllClean,
  onSelectProcessor,
  onOverrideChange,
  onViewClient = () => {},
  onOpenNotes = () => {},
  rowsPerPage = 50,
  viewSignature = '',
  hideSkippedFilter = false,
  onSkipSelected,
  skipSelectedCount = 0,
}) => {
  // Page (0-based index) and sort are URL-backed so Back restores them — see VIEW_URL_STATE.
  const { page: urlPage, sort: sortBy, setPage: setUrlPage, setSort: setSortBy } = useURLState(VIEW_URL_STATE)
  const page = Math.max(0, (urlPage || 1) - 1)
  const setPage = React.useCallback((nextIndex) => setUrlPage(nextIndex + 1), [setUrlPage])

  const sortedRows = React.useMemo(() => {
    const copy = [...rows]
    switch (sortBy) {
      case 'amountDesc':
        return copy.sort((a, b) => (Number(b.amount) || 0) - (Number(a.amount) || 0))
      case 'amountAsc':
        return copy.sort((a, b) => (Number(a.amount) || 0) - (Number(b.amount) || 0))
      case 'clientName':
        return copy.sort((a, b) => (a.clientName || '').localeCompare(b.clientName || ''))
      case 'forecastDesc':
        return copy.sort((a, b) => (b.forecast?.successRate || 0) - (a.forecast?.successRate || 0))
      default:
        return copy
    }
  }, [rows, sortBy])

  // Render only one page of rows. The date range drives the row count (a 1-day range is
  // ~67 rows, 6 weeks is ~4,000), and every row mounts a Checkbox, a Select, Chips and
  // Tooltips — so mounting the whole set is what makes a wide range unusable.
  //
  // Deliberately CLIENT-side: `rows` stays the full set, so the summary totals, quick-filter
  // counts, "select all clean" and the approve batch all keep operating over everything.
  // Paginating server-side would quietly reduce those to the visible page, which on this
  // screen means approving less money than the operator intended.
  const pageCount = Math.max(1, Math.ceil(sortedRows.length / rowsPerPage))
  // Filters/sort can shrink the list under the current page — clamp instead of showing blank.
  const safePage = Math.min(page, pageCount - 1)
  const pagedRows = React.useMemo(
    () => sortedRows.slice(safePage * rowsPerPage, safePage * rowsPerPage + rowsPerPage),
    [sortedRows, safePage, rowsPerPage]
  )

  // Reset to the first page ONLY when the operator changes WHAT they are looking at — the quick
  // filter, the sort, or the date/facet query (viewSignature from the parent). A row leaving the
  // list because it was approved or skipped, a background refetch, or the same set arriving in a
  // new order must NOT reset: the reviewer stays put (safePage clamps if their page vanished).
  //
  // Guarded against the first run so a page restored from the URL (returning via Back) survives.
  // The guard is a value compare, not a "skip the first render" flag, so it also holds under
  // StrictMode's mount/unmount/mount double-invoke.
  const viewKey = `${quickFilter}|${sortBy}|${viewSignature}`
  const prevViewKeyRef = React.useRef()
  React.useEffect(() => {
    if (prevViewKeyRef.current === undefined) {
      prevViewKeyRef.current = viewKey
      return
    }
    if (prevViewKeyRef.current !== viewKey) {
      prevViewKeyRef.current = viewKey
      setPage(0)
    }
  }, [viewKey, setPage])

  // Skeleton rows only cover the very first load (no rows yet); once any rows
  // are present we keep showing them while a background refresh is in flight.
  const showSkeleton = loading && sortedRows.length === 0

  return (
    <div className="rounded-2xl border border-divider bg-paper overflow-hidden">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3 border-b border-divider">
        <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Filter payments">
          {QUICK_FILTERS.map(({ key }) => {
            if (hideSkippedFilter && key === SKIPPED_FILTER_KEY) return null
            const active = quickFilter === key
            const count = counts?.[key] ?? 0
            return (
              <Chip
                key={key}
                size="small"
                data-testid={`filter-chip-${key}`}
                onClick={() => onChangeFilter?.(key)}
                aria-pressed={active}
                color={active ? 'primary' : 'default'}
                variant={active ? 'filled' : 'outlined'}
                label={
                  <span className="inline-flex items-center gap-1.5">
                    <span>{SIDEBAR_FILTER_LABELS[key]}</span>
                    <span
                      // The active chip is filled with primary.main, so this badge draws
                      // on a coloured backdrop rather than the card. A WHITE veil moves
                      // that backdrop toward the white text: at 25% it measured 3.00:1,
                      // and more veil is worse, not better (35% -> 2.53). Darkening
                      // instead keeps the same recessed look and reads 6.65:1. Both
                      // values are theme-independent, so this holds in light and dark.
                      className={`inline-flex min-w-[18px] justify-center rounded-full px-1 text-[11px] leading-4 ${
                        active ? 'bg-black/20 text-white' : 'bg-divider/60 text-text-secondary'
                      }`}
                    >
                      {count}
                    </span>
                  </span>
                }
              />
            )
          })}
        </div>
        <div className="ml-auto flex items-center gap-3">
          <SelectByProcessorMenu groups={processorGroups} onSelectProcessor={onSelectProcessor} />
          <Button size="small" variant="outlined" data-testid="select-all-clean" onClick={onSelectAllClean}>
            Select all clean ({cleanCount})
          </Button>
          {onSkipSelected && (
            <Button
              size="small"
              variant="outlined"
              data-testid="skip-selected"
              disabled={skipSelectedCount === 0}
              onClick={() => onSkipSelected()}
            >
              {`Skip selected (${skipSelectedCount})`}
            </Button>
          )}
          <div className="flex items-center gap-2">
            <span className="text-xs text-text-secondary">Sort</span>
            <Select size="small" value={sortBy} onChange={(e) => setSortBy(e.target.value)} data-testid="sort-select">
              <MenuItem value="default">Default</MenuItem>
              <MenuItem value="amountDesc">Amount (high to low)</MenuItem>
              <MenuItem value="amountAsc">Amount (low to high)</MenuItem>
              <MenuItem value="clientName">Client Name (A–Z)</MenuItem>
              <MenuItem value="forecastDesc">AI Forecast (high to low)</MenuItem>
            </Select>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={allVisibleSelected}
                  indeterminate={!allVisibleSelected && someVisibleSelected}
                  onChange={(e) => onToggleAllVisible(e.target.checked)}
                  inputProps={{ 'aria-label': 'select all visible rows' }}
                />
              </TableCell>
              <TableCell>Client</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Payment</TableCell>
              <TableCell>Due Date</TableCell>
              <TableCell>Last Payment</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Plan Status</TableCell>
              <TableCell>AI Forecast</TableCell>
              <TableCell>
                <Tooltip title="Each payment is auto-routed to the recommended gateway. Override or skip per row.">
                  <span className="inline-flex items-center gap-1 cursor-help border-b border-dotted border-text-secondary/50">
                    Processor
                  </span>
                </Tooltip>
              </TableCell>
              <TableCell align="center">Attempts</TableCell>
              <TableCell align="center">Notes</TableCell>
              <TableCell align="center">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {showSkeleton ? (
              <TableSkeletonRows colSpan={13} />
            ) : (
              <>
                {pagedRows.map((row) => (
                  <PaymentRow
                    key={row.paymentId}
                    row={row}
                    checked={!!selection[row.paymentId]}
                    selectValue={getEffectiveSelectValue(row, overrides)}
                    result={resultsById[row.paymentId]}
                    onToggleRow={onToggleRow}
                    onOverrideChange={onOverrideChange}
                    onViewClient={onViewClient}
                    onOpenNotes={onOpenNotes}
                  />
                ))}
                {pagedRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={13}>
                      <p className="text-sm text-text-secondary text-center py-8">
                        {(totalRows ?? rows.length) === 0
                          ? 'No payments due today.'
                          : 'No payments match the current filter.'}
                      </p>
                    </TableCell>
                  </TableRow>
                )}
              </>
            )}
          </TableBody>
        </Table>
      </div>

      {sortedRows.length > rowsPerPage && (
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-divider">
          <span className="text-xs text-text-secondary" data-testid="pagination-summary">
            Showing {safePage * rowsPerPage + 1}–{Math.min((safePage + 1) * rowsPerPage, sortedRows.length)} of{' '}
            {sortedRows.length}
          </span>
          <div className="flex items-center gap-2">
            <Button
              size="small"
              variant="outlined"
              data-testid="page-prev"
              disabled={safePage === 0}
              onClick={() => setPage(Math.max(0, safePage - 1))}
            >
              Previous
            </Button>
            <span className="text-xs text-text-secondary" data-testid="page-indicator">
              Page {safePage + 1} of {pageCount}
            </span>
            <Button
              size="small"
              variant="outlined"
              data-testid="page-next"
              disabled={safePage >= pageCount - 1}
              onClick={() => setPage(Math.min(pageCount - 1, safePage + 1))}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

export default PaymentsTable
